import React, { useEffect, useState } from "react";
import { formatDate } from "@/lib/formatDate";
import { apiClient } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Code2,
  Copy,
  Key,
  Loader2,
  Plus,
  Trash2,
  Webhook,
  ChevronDown,
  ChevronRight,
  RotateCw,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";

const EVENTS = [
  "receipt.created",
  "receipt.posted",
  "invoice.created",
  "invoice.paid",
  "bill.created",
  "bill.paid",
  "journal.posted",
];

export function SettingsDeveloperSection({ companyId }) {
  const [tab, setTab] = useState("keys");

  // API Keys
  const [keys, setKeys] = useState([]);
  const [keyForm, setKeyForm] = useState({ name: "", permissions: ["read"] });
  const [newKeyPlain, setNewKeyPlain] = useState(null);
  const [loadingKeys, setLoadingKeys] = useState(false);

  // Webhooks
  const [hooks, setHooks] = useState([]);
  const [hookForm, setHookForm] = useState({
    target_url: "",
    events: [],
    description: "",
  });
  const [newSecret, setNewSecret] = useState(null);
  const [loadingHooks, setLoadingHooks] = useState(false);
  const [expandedHook, setExpandedHook] = useState(null);
  const [deliveries, setDeliveries] = useState({});
  const [loadingDeliveries, setLoadingDeliveries] = useState(null);
  const [replaying, setReplaying] = useState(null);

  const loadKeys = async () => {
    if (!companyId) return;
    setLoadingKeys(true);
    try {
      const r = await apiClient.get(`/companies/${companyId}/api-clients`);
      setKeys(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Laden der API-Keys");
    } finally {
      setLoadingKeys(false);
    }
  };
  const loadHooks = async () => {
    if (!companyId) return;
    setLoadingHooks(true);
    try {
      const r = await apiClient.get(`/companies/${companyId}/webhooks`);
      setHooks(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Laden der Webhooks");
    } finally {
      setLoadingHooks(false);
    }
  };

  useEffect(() => {
    loadKeys();
    loadHooks();
  }, [companyId]);

  const createKey = async (e) => {
    e.preventDefault();
    if (!keyForm.name) {
      toast.error("Bitte einen Namen angeben.");
      return;
    }
    try {
      const r = await apiClient.post(
        `/companies/${companyId}/api-clients`,
        keyForm
      );
      setNewKeyPlain(r.data.plaintext_key);
      toast.success("API-Key erstellt. Jetzt kopieren — er wird nicht erneut angezeigt.");
      setKeyForm({ name: "", permissions: ["read"] });
      await loadKeys();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erstellen fehlgeschlagen.");
    }
  };

  const deleteKey = async (id) => {
    if (!window.confirm("API-Key wirklich widerrufen? Anwendungen mit diesem Key verlieren sofort Zugriff.")) return;
    try {
      await apiClient.delete(`/companies/${companyId}/api-clients/${id}`);
      toast.success("API-Key widerrufen.");
      await loadKeys();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Löschen fehlgeschlagen.");
    }
  };

  const toggleEvent = (ev) => {
    setHookForm((f) => ({
      ...f,
      events: f.events.includes(ev)
        ? f.events.filter((e) => e !== ev)
        : [...f.events, ev],
    }));
  };

  const togglePermission = (p) => {
    setKeyForm((f) => ({
      ...f,
      permissions: f.permissions.includes(p)
        ? f.permissions.filter((x) => x !== p)
        : [...f.permissions, p],
    }));
  };

  const createHook = async (e) => {
    e.preventDefault();
    if (!hookForm.target_url || hookForm.events.length === 0) {
      toast.error("URL + mindestens 1 Event pflichtig.");
      return;
    }
    try {
      const r = await apiClient.post(
        `/companies/${companyId}/webhooks`,
        hookForm
      );
      setNewSecret(r.data.secret);
      toast.success("Webhook angelegt. Secret jetzt kopieren.");
      setHookForm({ target_url: "", events: [], description: "" });
      await loadHooks();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Anlegen fehlgeschlagen.");
    }
  };

  const deleteHook = async (id) => {
    if (!window.confirm("Webhook wirklich löschen?")) return;
    try {
      await apiClient.delete(`/companies/${companyId}/webhooks/${id}`);
      toast.success("Webhook entfernt.");
      await loadHooks();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Löschen fehlgeschlagen.");
    }
  };

  const loadDeliveries = async (webhookId) => {
    setLoadingDeliveries(webhookId);
    try {
      const r = await apiClient.get(
        `/companies/${companyId}/webhooks/${webhookId}/deliveries?limit=10`
      );
      setDeliveries((d) => ({ ...d, [webhookId]: r.data || [] }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Deliveries laden fehlgeschlagen.");
    } finally {
      setLoadingDeliveries(null);
    }
  };

  const toggleExpand = async (webhookId) => {
    if (expandedHook === webhookId) {
      setExpandedHook(null);
    } else {
      setExpandedHook(webhookId);
      if (!deliveries[webhookId]) {
        await loadDeliveries(webhookId);
      }
    }
  };

  const replay = async (webhookId, deliveryId) => {
    setReplaying(deliveryId);
    try {
      const r = await apiClient.post(
        `/companies/${companyId}/webhooks/${webhookId}/deliveries/${deliveryId}/replay`
      );
      if (r.data.success) {
        toast.success(`Replay erfolgreich (HTTP ${r.data.status_code || "2xx"}).`);
      } else {
        toast.error(`Replay fehlgeschlagen (HTTP ${r.data.status_code || "-"}).`);
      }
      await loadDeliveries(webhookId);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Replay fehlgeschlagen.");
    } finally {
      setReplaying(null);
    }
  };

  const copy = (text) => {
    navigator.clipboard?.writeText(text);
    toast.success("In Zwischenablage kopiert.");
  };

  return (
    <Card
      className="p-5 border border-slate-200 bg-white"
      id="section-developer"
      data-testid="settings-developer-section"
    >
      <div className="flex items-start gap-3 mb-3">
        <div className="h-9 w-9 rounded-lg bg-indigo-50 text-indigo-700 flex items-center justify-center flex-shrink-0">
          <Code2 className="h-4 w-4" />
        </div>
        <div className="flex-1">
          <h2 className="text-sm font-medium text-slate-900">Entwickler / Public API</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            API-Keys für Drittsysteme (Treuhänder-Software, ERP-Anbindungen) und Webhook-Subscriptions. HMAC-SHA256-signierte Deliveries.
          </p>
          <a
            href="/developers"
            className="text-xs text-indigo-600 hover:text-indigo-800 hover:underline mt-1 inline-block"
            data-testid="developer-portal-link"
          >
            → Developer Portal öffnen (Doku + cURL-Beispiele)
          </a>
        </div>
      </div>

      <div className="flex gap-2 mb-4 border-b border-slate-200">
        <button
          type="button"
          onClick={() => setTab("keys")}
          className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors ${
            tab === "keys"
              ? "border-indigo-600 text-indigo-700"
              : "border-transparent text-slate-500 hover:text-slate-900"
          }`}
          data-testid="developer-tab-keys"
        >
          <Key className="h-3.5 w-3.5 inline mr-1.5" /> API-Keys ({keys.length})
        </button>
        <button
          type="button"
          onClick={() => setTab("hooks")}
          className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors ${
            tab === "hooks"
              ? "border-indigo-600 text-indigo-700"
              : "border-transparent text-slate-500 hover:text-slate-900"
          }`}
          data-testid="developer-tab-hooks"
        >
          <Webhook className="h-3.5 w-3.5 inline mr-1.5" /> Webhooks ({hooks.length})
        </button>
      </div>

      {tab === "keys" && (
        <div className="space-y-4" data-testid="developer-keys-tab">
          {newKeyPlain && (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs" data-testid="new-api-key-banner">
              <div className="font-semibold text-amber-900 mb-1">⚠️ Jetzt kopieren — wird nicht erneut angezeigt!</div>
              <div className="flex items-center gap-2">
                <code className="bg-white border border-amber-200 px-2 py-1 rounded flex-1 font-mono break-all">
                  {newKeyPlain}
                </code>
                <Button size="sm" type="button" variant="outline" onClick={() => copy(newKeyPlain)} data-testid="copy-new-key-button">
                  <Copy className="h-3.5 w-3.5" />
                </Button>
                <Button size="sm" type="button" variant="ghost" onClick={() => setNewKeyPlain(null)}>OK</Button>
              </div>
            </div>
          )}

          <form onSubmit={createKey} className="grid grid-cols-1 md:grid-cols-[1fr,auto,auto] gap-2 items-end" data-testid="new-api-key-form">
            <div>
              <Label className="text-xs">Bezeichnung</Label>
              <Input
                placeholder="z.B. Treuhänder-Integration"
                value={keyForm.name}
                onChange={(e) => setKeyForm((f) => ({ ...f, name: e.target.value }))}
                data-testid="new-api-key-name-input"
              />
            </div>
            <div className="flex gap-1.5">
              {[
                { id: "read", label: "read" },
                { id: "write:customers", label: "write:customers" },
                { id: "write:invoices", label: "write:invoices" },
                { id: "write:payments", label: "write:payments" },
                { id: "write", label: "write (Legacy: alle write:*)" },
                { id: "admin", label: "admin" },
              ].map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => togglePermission(p.id)}
                  className={`px-2.5 py-1.5 text-xs rounded border transition-colors ${
                    keyForm.permissions.includes(p.id)
                      ? "bg-indigo-600 text-white border-indigo-600"
                      : "bg-white text-slate-700 border-slate-300 hover:border-slate-400"
                  }`}
                  data-testid={`new-api-key-perm-${p.id}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <Button type="submit" data-testid="new-api-key-submit">
              <Plus className="h-4 w-4 mr-1" /> Key erstellen
            </Button>
          </form>

          <div className="border rounded-md border-slate-200">
            {loadingKeys ? (
              <div className="p-6 text-center text-slate-400"><Loader2 className="h-4 w-4 inline animate-spin mr-2" />Lade …</div>
            ) : keys.length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-400" data-testid="empty-api-keys">Noch keine API-Keys.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs text-slate-500">
                  <tr>
                    <th className="text-left p-2">Name</th>
                    <th className="text-left p-2">Prefix</th>
                    <th className="text-left p-2">Permissions</th>
                    <th className="text-left p-2">Erstellt</th>
                    <th className="p-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {keys.map((k) => (
                    <tr key={k.id} className="border-t border-slate-100" data-testid={`api-key-row-${k.id}`}>
                      <td className="p-2 font-medium">{k.name}</td>
                      <td className="p-2"><code className="text-xs bg-slate-50 px-1.5 py-0.5 rounded">{k.key_prefix}…</code></td>
                      <td className="p-2">
                        {(k.permissions || []).map((p) => (
                          <Badge key={p} variant="outline" className="text-xs mr-1">{p}</Badge>
                        ))}
                      </td>
                      <td className="p-2 text-xs text-slate-500">{formatDate(k.created_at)}</td>
                      <td className="p-2 text-right">
                        <Button size="icon" variant="ghost" onClick={() => deleteKey(k.id)} data-testid={`delete-api-key-${k.id}`}>
                          <Trash2 className="h-4 w-4 text-rose-500" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {tab === "hooks" && (
        <div className="space-y-4" data-testid="developer-hooks-tab">
          {newSecret && (
            <div className="rounded-md border border-emerald-300 bg-emerald-50 p-3 text-xs" data-testid="new-webhook-secret-banner">
              <div className="font-semibold text-emerald-900 mb-1">Webhook-Secret — jetzt kopieren!</div>
              <div className="flex items-center gap-2">
                <code className="bg-white border border-emerald-200 px-2 py-1 rounded flex-1 font-mono break-all">
                  {newSecret}
                </code>
                <Button size="sm" type="button" variant="outline" onClick={() => copy(newSecret)} data-testid="copy-new-secret-button">
                  <Copy className="h-3.5 w-3.5" />
                </Button>
                <Button size="sm" type="button" variant="ghost" onClick={() => setNewSecret(null)}>OK</Button>
              </div>
              <p className="text-[10px] text-emerald-700 mt-2">
                Verifizierung: <code>X-Signature: sha256=HMAC(secret, body)</code>
              </p>
            </div>
          )}

          <form onSubmit={createHook} className="space-y-3 border border-slate-200 rounded-md p-3" data-testid="new-webhook-form">
            <div>
              <Label className="text-xs">Ziel-URL</Label>
              <Input
                placeholder="https://example.com/webhook"
                value={hookForm.target_url}
                onChange={(e) => setHookForm((f) => ({ ...f, target_url: e.target.value }))}
                data-testid="new-webhook-url-input"
              />
            </div>
            <div>
              <Label className="text-xs">Events (mind. 1)</Label>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {EVENTS.map((ev) => (
                  <button
                    key={ev}
                    type="button"
                    onClick={() => toggleEvent(ev)}
                    className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                      hookForm.events.includes(ev)
                        ? "bg-indigo-600 text-white border-indigo-600"
                        : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"
                    }`}
                    data-testid={`new-webhook-event-${ev.replace(".", "-")}`}
                  >
                    {ev}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs">Beschreibung (optional)</Label>
              <Input
                placeholder="z.B. CRM-Sync produktiv"
                value={hookForm.description}
                onChange={(e) => setHookForm((f) => ({ ...f, description: e.target.value }))}
                data-testid="new-webhook-description-input"
              />
            </div>
            <Button type="submit" data-testid="new-webhook-submit">
              <Plus className="h-4 w-4 mr-1" /> Webhook anlegen
            </Button>
          </form>

          <div className="border rounded-md border-slate-200">
            {loadingHooks ? (
              <div className="p-6 text-center text-slate-400"><Loader2 className="h-4 w-4 inline animate-spin mr-2" />Lade …</div>
            ) : hooks.length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-400" data-testid="empty-webhooks">Noch keine Webhooks.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs text-slate-500">
                  <tr>
                    <th className="w-8"></th>
                    <th className="text-left p-2">URL</th>
                    <th className="text-left p-2">Events</th>
                    <th className="text-left p-2">Secret</th>
                    <th className="text-left p-2">Fehler</th>
                    <th className="p-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {hooks.map((h) => (
                    <React.Fragment key={h.id}>
                      <tr className="border-t border-slate-100" data-testid={`webhook-row-${h.id}`}>
                        <td className="p-2 align-top">
                          <button
                            type="button"
                            className="inline-flex items-center"
                            onClick={() => toggleExpand(h.id)}
                            data-testid={`webhook-expand-${h.id}`}
                            aria-label="Deliveries anzeigen"
                          >
                            {expandedHook === h.id ? (
                              <ChevronDown className="h-4 w-4 text-slate-500" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-slate-500" />
                            )}
                          </button>
                        </td>
                        <td className="p-2 font-mono text-xs break-all max-w-[240px]">{h.target_url}</td>
                        <td className="p-2">
                          {(h.events || []).map((e) => (
                            <Badge key={e} variant="outline" className="text-[10px] mr-1 mb-0.5">{e}</Badge>
                          ))}
                        </td>
                        <td className="p-2"><code className="text-xs">{h.secret_masked}</code></td>
                        <td className="p-2 text-xs">
                          {h.failure_count > 0 ? (
                            <Badge variant="destructive">{h.failure_count}</Badge>
                          ) : (
                            <span className="text-emerald-600">ok</span>
                          )}
                        </td>
                        <td className="p-2 text-right">
                          <Button size="icon" variant="ghost" onClick={() => deleteHook(h.id)} data-testid={`delete-webhook-${h.id}`}>
                            <Trash2 className="h-4 w-4 text-rose-500" />
                          </Button>
                        </td>
                      </tr>
                      {expandedHook === h.id && (
                        <tr data-testid={`webhook-deliveries-row-${h.id}`}>
                          <td colSpan={6} className="bg-slate-50 p-3">
                            <DeliveriesPanel
                              deliveries={deliveries[h.id]}
                              loading={loadingDeliveries === h.id}
                              onReplay={(did) => replay(h.id, did)}
                              replaying={replaying}
                              onRefresh={() => loadDeliveries(h.id)}
                              webhookId={h.id}
                            />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

function DeliveriesPanel({ deliveries, loading, onReplay, replaying, onRefresh, webhookId }) {
  const fmt = (iso) => {
    if (!iso) return "-";
    try {
      return new Date(iso).toLocaleString("de-CH", { dateStyle: "short", timeStyle: "medium" });
    } catch {
      return iso;
    }
  };

  if (loading) {
    return (
      <div className="text-center text-xs text-slate-500 py-3" data-testid={`deliveries-loading-${webhookId}`}>
        <Loader2 className="h-4 w-4 inline animate-spin mr-2" /> Lade Deliveries …
      </div>
    );
  }
  if (!deliveries || deliveries.length === 0) {
    return (
      <div className="text-center text-xs text-slate-400 py-3" data-testid={`deliveries-empty-${webhookId}`}>
        Noch keine Deliveries für diesen Webhook.
        <Button variant="ghost" size="sm" className="ml-2" onClick={onRefresh}>
          <RotateCw className="h-3.5 w-3.5 mr-1" /> Aktualisieren
        </Button>
      </div>
    );
  }
  return (
    <div data-testid={`deliveries-panel-${webhookId}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-semibold text-slate-700">Letzte {deliveries.length} Deliveries</div>
        <Button variant="ghost" size="sm" onClick={onRefresh} data-testid={`deliveries-refresh-${webhookId}`}>
          <RotateCw className="h-3.5 w-3.5 mr-1" /> Aktualisieren
        </Button>
      </div>
      <table className="w-full text-xs">
        <thead className="text-[10px] text-slate-500 uppercase">
          <tr>
            <th className="text-left p-1">Status</th>
            <th className="text-left p-1">Event</th>
            <th className="text-left p-1">HTTP</th>
            <th className="text-left p-1">Versuche</th>
            <th className="text-left p-1">Zeit</th>
            <th className="text-left p-1">Response / Fehler</th>
            <th className="p-1"></th>
          </tr>
        </thead>
        <tbody>
          {deliveries.map((d) => (
            <tr key={d.id} className="border-t border-slate-200" data-testid={`delivery-row-${d.id}`}>
              <td className="p-1">
                {d.success ? (
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                ) : (
                  <XCircle className="h-4 w-4 text-rose-500" />
                )}
              </td>
              <td className="p-1 font-mono">{d.event}</td>
              <td className="p-1">
                {d.status_code ? (
                  <Badge
                    variant="outline"
                    className={d.success ? "text-emerald-700 border-emerald-300" : "text-rose-700 border-rose-300"}
                  >
                    {d.status_code}
                  </Badge>
                ) : (
                  <span className="text-slate-400">-</span>
                )}
              </td>
              <td className="p-1 text-center">{d.attempts_count}</td>
              <td className="p-1 text-slate-500 whitespace-nowrap">{fmt(d.dispatched_at)}</td>
              <td className="p-1 max-w-[240px] truncate text-slate-600" title={d.error || d.response_excerpt || ""}>
                {d.error ? <span className="text-rose-600">{d.error}</span> : (d.response_excerpt || <span className="text-slate-400">-</span>)}
              </td>
              <td className="p-1 text-right">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={replaying === d.id}
                  onClick={() => onReplay(d.id)}
                  data-testid={`delivery-replay-${d.id}`}
                >
                  {replaying === d.id ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <RotateCw className="h-3.5 w-3.5 mr-1" />
                  )}
                  Retry
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
